import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";

export const BlogCard = ({ post, t, locale }) => (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    data-testid={`blog-card-${post.slug}`}
    className="group rounded-3xl overflow-hidden bg-white border border-neutral-200 hover:shadow-[0_18px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all"
  >
    <Link to={`/blog/${post.slug}`} className="block">
      {post.cover_image && (
        <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
          <img
            src={post.cover_image}
            alt={post.title[locale] || post.title.de}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-7">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-sky-700">
          {(post.tags || []).slice(0, 2).map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
          <span className="inline-flex items-center gap-1 text-neutral-400 normal-case tracking-normal">
            <Clock className="w-3 h-3" /> {post.read_minutes} {t("blog.minutes")}
          </span>
        </div>
        <h3 className="font-display mt-4 text-xl lg:text-2xl font-medium text-neutral-950 tracking-tight text-balance">
          {post.title[locale] || post.title.de}
        </h3>
        <p className="mt-3 text-neutral-600 text-[15px] leading-relaxed line-clamp-3">
          {post.excerpt[locale] || post.excerpt.de}
        </p>
        <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700">
          {t("blog.read")}
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  </motion.article>
);

const BlogTeaser = () => {
  const { t, locale } = useLanguage();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api
      .get("/blog/posts", { params: { limit: 3 } })
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]));
  }, []);

  if (posts.length === 0) return null;

  return (
    <section
      id="blog"
      data-testid="blog-teaser-section"
      className="container-px mx-auto max-w-7xl py-24 lg:py-32"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
            {t("blog.eyebrow")}
          </p>
          <h2 className="font-display mt-3 text-4xl lg:text-6xl font-light tracking-tighter text-neutral-950 text-balance">
            {t("blog.title")}
          </h2>
        </div>
        <Link
          to="/blog"
          data-testid="blog-teaser-all-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-950"
        >
          {t("nav.blog")}
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.map((p) => (
          <BlogCard key={p.slug} post={p} t={t} locale={locale} />
        ))}
      </div>
    </section>
  );
};

export default BlogTeaser;
